import React, { useEffect, useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive'
import { css, keyframes } from '@emotion/react'
import { useNavigate } from 'react-router-dom'

import { ReactComponent as PCLine } from '@assets/svg/HomePCLine.svg'
import { ReactComponent as MobileLine } from '@assets/svg/HomeMobileLine.svg'
import MobilePlay from '@assets/image/MobilePlay.png'
import PCPlay from '@assets/image/PCPlay.png'

import HeaderText from '@components/text/HeaderText'
import BodyText from '@components/text/BodyText'
import TitleText from '@components/text/TitleText'

import { awsUrl } from '@utils/function/globalFunction'
import { lang, langs } from '@utils/locales/homeLang'

import MinimalText from '@components/text/MinimalText'
import WebVideoModal from '@components/modal/WebVideoModal'

import { VIdeoModalT } from '@lib/modal'
import { useSelector } from 'react-redux'
import { RootState } from '@store/Store'

const HomePage = ({
  setSelectedDiv,
  coreTechnologyRef,
  mainSkillRef,
  partnerRef,
}: {
  setSelectedDiv: React.Dispatch<React.SetStateAction<number>>
  coreTechnologyRef: React.RefObject<HTMLDivElement>
  mainSkillRef: React.RefObject<HTMLDivElement>
  partnerRef: React.RefObject<HTMLDivElement>
}) => {
  const storeLanguage = useSelector((state: RootState) => state.language)

  const isMobile = useMediaQuery({ query: '(max-width:1099px)' })
  const navigate = useNavigate()
  const videoRef = useRef<HTMLDivElement>(null)
  const videoTextRef = useRef<HTMLDivElement>(null)

  const [videoModal, setVideoModal] = useState<VIdeoModalT>({
    src: '',
    isModal: false,
  })
  const handleScroll = () => {
    const { scrollY } = window
    if (scrollY === 0) {
      setSelectedDiv(0)
    }
    if (coreTechnologyRef.current) {
      const coreTechTop = coreTechnologyRef.current.offsetTop - 204
      if (coreTechTop <= scrollY) {
        setSelectedDiv(1)
      }
    }
    if (mainSkillRef.current) {
      const mainSkillTop = mainSkillRef.current.offsetTop - 204
      if (mainSkillTop <= scrollY) {
        setSelectedDiv(2)
      }
    }
    if (partnerRef.current && mainSkillRef.current) {
      const partnerTop = partnerRef.current.offsetTop - 664
      if (partnerTop <= scrollY) {
        setSelectedDiv(3)
      }
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div css={homeStyle()}>
      <div className="connectedcar_container">
        <div className="video_container" ref={videoRef}>
          <video
            muted
            src="https://storage.googleapis.com/seniorcare_kiosk/connectedcar/connected.mp4"
            loop
            autoPlay
            playsInline
            className="connectedcar_video"
          ></video>

          <div className="connectedcar_text" ref={videoTextRef}>
            <div
              css={css`
                max-width: ${isMobile ? 'none' : '1100px'};
                margin: ${isMobile ? 'auto' : '0 auto'};
              `}
            >
              <HeaderText text={lang.lang10[storeLanguage]} />
              <BodyText
                text={lang.lang20[storeLanguage]}
                isBold={false}
                color="#A8A8A8"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="tech_container" ref={coreTechnologyRef}>
        <TitleText text="핵심 기술" mobileTitleVersion={2} />
        <div className="tech_inner">
          {langs.langs10.map((allItem, allItemIdx) => {
            const divIdx1 = allItemIdx + 1
            let titleTextColor = ''

            if (!allItemIdx) {
              titleTextColor = '#6875FF'
            } else if (allItemIdx === 1) {
              titleTextColor = '#42966A'
            } else {
              titleTextColor = '#6C0CA7'
            }
            return (
              <div className="tech_content" key={divIdx1}>
                <TitleText
                  text={allItem.title[storeLanguage]}
                  color={titleTextColor}
                  mobileTitleVersion={2}
                />
                <div className="tech_allItem">
                  {allItem.body.map((item, itemIdx) => {
                    const divIdx2 = itemIdx + 1
                    return (
                      <div className="tech_item" key={divIdx2}>
                        <img
                          src={awsUrl('tech', allItemIdx, false, itemIdx)}
                          alt={`${itemIdx}이미지`}
                          css={css`
                            height: ${isMobile ? '200px' : '300px'};
                          `}
                        />
                        <div className="tech_text">
                          <TitleText
                            text={item.title[storeLanguage]}
                            mobileTitleVersion={2}
                          />
                          <BodyText
                            text={item.sub[storeLanguage]}
                            isBold={false}
                            color="#a8a8a8"
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
        <div className="tech_line">
          {isMobile ? <MobileLine /> : <PCLine />}
        </div>
      </div>
      <div className="ralated_container">
        <div className="related_text">
          <TitleText text="관련 영상" mobileTitleVersion={2} />
        </div>

        <div className="related_slide">
          {Array.from(Array(5)).map((item, idx) => {
            const imgIdx = idx + 1
            return (
              <img
                className="slide_img"
                src={awsUrl('related', idx, isMobile)}
                alt={`관련영상 슬라이더이미지 ${idx}`}
                key={imgIdx}
              />
            )
          })}
          {Array.from(Array(5)).map((item, idx) => {
            const imgIdx = idx + 1
            return (
              <img
                className="clone_slide_img"
                src={awsUrl('related', idx, isMobile)}
                alt={`관련영상 슬라이더이미지 ${idx}`}
                key={imgIdx}
              />
            )
          })}
        </div>
      </div>
      <div className="skill_container" ref={mainSkillRef}>
        <TitleText text="주요 기능" mobileTitleVersion={2} />
        <div className="skill_inner">
          {langs.langs20.map((allItem, allItemIdx) => {
            const divKey = allItemIdx + 1
            let backColor = ''
            let videoSrc = ''
            if (!allItemIdx) {
              backColor = `linear-gradient(0deg, rgba(1, 51, 166, 0.08) 0%, rgba(1, 51, 166, 0.08) 100%), linear-gradient(180deg, #202020 0%, rgba(32, 32, 32, 0.40) 100%);`
              videoSrc =
                'https://storage.googleapis.com/seniorcare_kiosk/connectedcar/community.mp4'
            } else if (allItemIdx === 1) {
              backColor = `linear-gradient(0deg, rgba(255, 0, 118, 0.08) 0%, rgba(255, 0, 118, 0.08) 100%), linear-gradient(180deg, #202020 0%, rgba(32, 32, 32, 0.40) 100%);`
              videoSrc =
                'https://storage.googleapis.com/seniorcare_kiosk/connectedcar/angry.mp4'
            } else if (allItemIdx === 2) {
              backColor = `linear-gradient(0deg, rgba(0, 89, 255, 0.08) 0%, rgba(0, 89, 255, 0.08) 100%), linear-gradient(180deg, #202020 0%, rgba(32, 32, 32, 0.40) 100%);`
              videoSrc =
                'https://storage.googleapis.com/seniorcare_kiosk/connectedcar/tired.mp4'
            } else {
              backColor = `linear-gradient(0deg, rgba(99, 97, 107, 0.08) 0%, rgba(99, 97, 107, 0.08) 100%), linear-gradient(180deg, #202020 0%, rgba(32, 32, 32, 0.40) 100%);`
              videoSrc =
                'https://storage.googleapis.com/seniorcare_kiosk/connectedcar/morning.mp4'
            }
            return (
              <div className="skill_content" key={divKey}>
                <div className="img_box">
                  <img
                    className="img_background"
                    src={awsUrl('skill', allItemIdx, true)}
                    alt={`Skill${allItemIdx}이미지`}
                  />
                  <TitleText
                    text={allItem.title[storeLanguage]}
                    mobileTitleVersion={2}
                  />
                  <div className="btn_blur">
                    <img
                      className="img_btn
                  "
                      src={isMobile ? MobilePlay : PCPlay}
                      alt="플레이버튼"
                      css={css`
                        cursor: pointer;
                      `}
                      onClick={() =>
                        isMobile
                          ? navigate('/video', { state: { src: videoSrc } })
                          : setVideoModal({ src: videoSrc, isModal: true })
                      }
                    />
                  </div>
                </div>
                <div
                  className="text_box"
                  css={css`
                    border-radius: ${isMobile ? '8px' : '16px'};
                    background: ${backColor};
                  `}
                >
                  {allItem.body.map((item, itemIdx) => {
                    const divIdx2 = itemIdx + 1
                    return (
                      <div className="text_gap" key={divIdx2}>
                        <BodyText text={item.title[storeLanguage]} />
                        {isMobile ? (
                          <MinimalText
                            text={item.sub[storeLanguage]}
                            color="#a8a8a8"
                          />
                        ) : (
                          <BodyText
                            text={item.sub[storeLanguage]}
                            isBold={false}
                            color="#a8a8a8"
                          />
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
        {!isMobile && videoModal.isModal && (
          <WebVideoModal
            videoModal={videoModal}
            setVideoModal={setVideoModal}
          />
        )}
      </div>
      <div className="partner_container" ref={partnerRef}>
        <TitleText text="inDJ 주요 파트너사" mobileTitleVersion={2} />
        <img
          className="partner_img"
          src={awsUrl('partner', 0, isMobile)}
          alt="파트너십"
        />
      </div>
    </div>
  )
}

const divSlider = keyframes`
  0% {
      transform: translateX(0);
  }
  100% {
      transform: translateX(-50%);
  }
`
const homeStyle = () => css`
  .connectedcar_container {
    position: relative;
    .video_container {
      position: static;
      height: 694px;
      ::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background:
          linear-gradient(
            180deg,
            rgba(0, 0, 0, 0) 0%,
            rgba(0, 0, 0, 0.98) 100%
          ),
          100% / cover;
      }
      .connectedcar_video {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    .connectedcar_text {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      padding: 0px 20px;
      z-index: 1;
    }
  }
  .tech_container {
    margin-top: 32px;
    padding: 0px 20px;
    .tech_inner {
      margin-top: 32px;
      display: flex;
      flex-direction: column;
      gap: 120px;
      .tech_content {
        display: flex;
        align-items: center;
        flex-direction: column;
        gap: 32px;
        .tech_allItem {
          display: flex;
          flex-direction: column;
          gap: 60px;
          .tech_item {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 16px;
            .tech_text {
              display: flex;
              flex-direction: column;
              text-align: center;
              gap: 8px;
            }
          }
        }
      }
    }
    .tech_line {
      margin-top: 120px;
      display: flex;

      justify-content: center;
    }
  }
  .ralated_container {
    margin-top: 120px;
    .related_text {
      padding: 0px 20px;
    }

    .related_slide {
      margin-top: 32px;
      display: flex;
      gap: 20px;
      width: 3240px;
      height: 166px;
      animation: ${divSlider} 20s linear infinite;
      .slide_img,
      .clone_slide_img {
        width: 304px;
        height: 100%;
      }
    }
  }
  .skill_container {
    margin-top: 120px;
    padding: 0px 20px;
    .skill_inner {
      margin-top: 32px;
      display: flex;
      flex-direction: column;
      gap: 60px;
      .skill_content {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 24px;
        .img_box {
          position: relative;
          .img_background {
            height: 308px;
          }
          h2 {
            width: 100%;
            position: absolute;
            top: 32px;
            display: flex;
            justify-content: center;
          }
          .btn_blur {
            ::before {
              content: '';
              position: absolute;
              bottom: 20px;
              left: 20px;
              border-radius: 50%;
              width: 54px;
              height: 54px;
              background: rgba(255, 255, 255, 0.2);
              backdrop-filter: blur(6px);
            }
            .img_btn {
              position: absolute;
              bottom: 20px;
              left: 20px;
              height: 54px;
            }
          }
        }
        .text_box {
          width: 100%;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 24px;
          .text_gap {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
        }
      }
    }
  }
  .partner_container {
    margin-top: 120px;
    padding: 0px 20px;
    .partner_img {
      margin-top: 32px;
      height: 126px;
    }
  }
  @media screen and (min-width: 1100px) {
    .connectedcar_container {
      position: relative;

      .video_container {
        height: 100vh;
      }
      .connectedcar_text {
        width: 100%;
        margin: 0 auto;
        padding: 0px;
        div:nth-of-type(1) {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
      }
    }
    .tech_container {
      margin: 120px auto 0px;
      padding: 0px;
      max-width: 1100px;
      .tech_inner {
        margin-top: 60px;
        gap: 120px;
        .tech_content {
          gap: 60px;
          > h2 {
            place-self: start;
          }
          .tech_allItem {
            width: 100%;
            gap: 60px;
            .tech_item:nth-of-type(1) {
              flex-direction: row;
              gap: 80px;
              .tech_text {
                text-align: start;
                gap: 12px;
              }
            }
            .tech_item:nth-of-type(2) {
              flex-direction: row-reverse;
              gap: 80px;
              .tech_text {
                text-align: end;
                gap: 12px;
              }
            }
          }
        }
      }
    }
    .ralated_container {
      .related_text {
        max-width: 1100px;
        margin: 0 auto;
        padding: 0px;
      }
      .related_slide {
        margin-top: 60px;
        gap: 60px;
        width: 8480px;
        height: 400px;
        animation: ${divSlider} 30s linear infinite;
        .slide_img,
        .clone_slide_img {
          width: 788px;
        }
      }
    }
    .skill_container {
      padding: 0px;
      > h2 {
        max-width: 1100px;
        margin: 0 auto;
      }
      .skill_inner {
        margin-top: 60px;
        .skill_content:nth-of-type(even) {
          flex-direction: row-reverse;
        }
        .skill_content:nth-of-type(3n + 1) {
          .text_box {
            gap: 48px;
          }
        }
        .skill_content:nth-of-type(2),
        .skill_content:nth-of-type(3) {
          .text_box {
            gap: 32px;
          }
        }

        .skill_content {
          width: 100%;
          max-width: 1100px;
          margin: 0 auto;
          flex-direction: row;
          gap: 16px;
          .img_box {
            max-width: 328px;
            width: 100%;
            height: 460px;
            .img_background {
              height: 100%;
            }
            h2 {
              width: 100%;
              top: 42px;
              display: flex;
              justify-content: center;
            }
            .btn_blur {
              ::before {
                content: '';
                position: absolute;
                bottom: 20px;
                left: 20px;
                border-radius: 50%;
                width: 54px;
                height: 54px;
                background: rgba(255, 255, 255, 0.2);
                backdrop-filter: blur(6px);
              }
              .img_btn {
                position: absolute;
                bottom: 20px;
                left: 20px;
                height: 54px;
              }
            }
          }

          .text_box {
            width: 100%;
            padding: 42px;
            display: flex;
            flex-direction: column;
            height: 460px;
            .text_gap {
              display: flex;
              flex-direction: column;
              gap: 8px;
            }
          }
        }
      }
    }
    .partner_container {
      margin: 120px auto 0px;
      padding: 0px;
      max-width: 1100px;
      .partner_img {
        margin-top: 56px;
        height: 162px;
      }
    }
  }
`
export default HomePage
